import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AutomatedNotesScheduleBulkCreateInputDto,
  AutomatedNotesScheduleInputDto,
  AutomatedNotesScheduleOutputDto,
  CreateAutomatedNotesScheduleWithDatasetsDto,
  DashboardDatasetConfigBulkUpsertInputDto,
  DashboardDatasetConfigOutputDto,
  ReportInputDtoLevelEnum,
} from '@kortxio/hub-api';
import { setError } from 'features/error/slice';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import { AsyncThunkConfig } from 'store/types';

export const getAllAutomatedNoteSchedulesByReportLevelAndParentId =
  createAsyncThunk<
    AutomatedNotesScheduleOutputDto[],
    { reportLevel: ReportInputDtoLevelEnum; parentId: number },
    AsyncThunkConfig
  >(
    'prompts/getAllAutomatedNoteSchedulesByReportLevelAndParentId',
    async ({ reportLevel, parentId }, { dispatch, rejectWithValue }) => {
      try {
        const response =
          await api.automaticNoteSchedules.getAllAutomatedNoteSchedulesByReportLevelAndParentId(
            reportLevel,
            parentId
          );

        return parseApiPayload(response);
      } catch (error) {
        const apiError = toApiError(error);

        if (!apiError.isAbortedError) {
          dispatch(
            setError({
              message: apiError.message,
              uiMessage: apiError.uiMessage,
            })
          );
        }

        return rejectWithValue(apiError);
      }
    }
  );

export const upsertAutomatedNoteSchedule = createAsyncThunk<
  string,
  { body: AutomatedNotesScheduleInputDto },
  AsyncThunkConfig
>(
  'prompts/upsertAutomatedNoteSchedule',
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.automaticNoteSchedules.upsertAutomatedNotesSchedule(body);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getDashboardDatasetConfigsByReportLevelAndParentId =
  createAsyncThunk<
    DashboardDatasetConfigOutputDto[],
    { reportLevel: ReportInputDtoLevelEnum; parentId: number },
    AsyncThunkConfig
  >(
    'prompts/getDashboardDatasetConfigsByReportLevelAndParentId',
    async ({ reportLevel, parentId }, { dispatch, rejectWithValue }) => {
      try {
        const response =
          await api.dashboardDatasetConfigs.getDashboardDatasetConfigsByReportLevelAndParentId(
            reportLevel,
            parentId
          );

        return parseApiPayload(response);
      } catch (error) {
        const apiError = toApiError(error);

        if (!apiError.isAbortedError) {
          dispatch(
            setError({
              message: apiError.message,
              uiMessage: apiError.uiMessage,
            })
          );
        }

        return rejectWithValue(apiError);
      }
    }
  );

export const createAutomatedNotesScheduleWithDataset = createAsyncThunk<
  string,
  { body: CreateAutomatedNotesScheduleWithDatasetsDto },
  AsyncThunkConfig
>(
  'prompts/createAutomatedNotesScheduleWithDataset',
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.automaticNoteSchedules.createAutomatedNotesScheduleWithDataset(
          body
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const bulkCreateAutomatedNotesSchedulesWithDatasets = createAsyncThunk<
  void,
  { body: AutomatedNotesScheduleBulkCreateInputDto },
  AsyncThunkConfig
>(
  'prompts/bulkCreateAutomatedNotesSchedulesWithDatasets',
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      await api.automaticNoteSchedules.bulkCreateAutomatedNotesSchedulesWithDatasets(
        body
      );
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const upsertDashboardDatasetConfig = createAsyncThunk<
  string,
  { body: DashboardDatasetConfigBulkUpsertInputDto },
  AsyncThunkConfig
>(
  'prompts/upsertDashboardDatasetConfig',
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.dashboardDatasetConfigs.upsertDashboardDatasetConfigs(body);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const bulkUpsertDashboardDatasetConfigs = createAsyncThunk<
  void,
  { body: DashboardDatasetConfigBulkUpsertInputDto },
  AsyncThunkConfig
>(
  'prompts/bulkUpsertDashboardDatasetConfigs',
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      await api.dashboardDatasetConfigs.upsertDashboardDatasetConfigs(body);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const deleteDashboardDatasetConfigsByIds = createAsyncThunk<
  void,
  { ids: string[] },
  AsyncThunkConfig
>(
  'prompts/deleteDashboardDatasetConfigsByIds',
  async ({ ids }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.dashboardDatasetConfigs.deleteDashboardDatasetConfigsByIds(
          ids
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const runAutomatedNoteScheduleById = createAsyncThunk<
  void,
  { automatedNotesScheduleId: string },
  AsyncThunkConfig
>(
  'prompts/runAutomatedNoteScheduleById',
  async ({ automatedNotesScheduleId }, { dispatch, rejectWithValue }) => {
    try {
      await api.automaticNoteSchedules.runAutomatedNotesScheduleById(
        automatedNotesScheduleId
      );
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const deleteAutomatedNoteSchedule = createAsyncThunk<
  void,
  { automatedNotesScheduleId: string },
  AsyncThunkConfig
>(
  'prompts/deleteAutomatedNoteSchedule',
  async ({ automatedNotesScheduleId }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.automaticNoteSchedules.deleteAutomatedNotesSchedule(
          automatedNotesScheduleId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);
